import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Space } from '@/components/base';
import Button from '@/components/Button/Button';
import useColor from '@/hooks/domain/useColor';
import useUserStore from '@/stores/UserStore';

import type { OktaLoginIntent } from './use-okta-idp-initiation';

type Auth0LoginFlowState =
  | {
      readonly status: 'idle' | 'pending';
      readonly errorMessage: null;
    }
  | {
      readonly status: 'failed';
      readonly errorMessage: string;
    };

type Auth0LoginAttemptSource = 'popup' | 'redirect';

type Auth0LoginAttemptState =
  | {
      readonly status: 'idle';
    }
  | {
      readonly status: 'pending';
      readonly id: number;
      readonly source: Auth0LoginAttemptSource;
    }
  | {
      readonly status: 'failed';
      readonly source: Auth0LoginAttemptSource | 'verification';
      readonly errorMessage: string;
    };

type Auth0LoginFlowPhase =
  | {
      readonly kind: 'idle' | 'redirecting' | 'authenticating' | 'verifying';
    }
  | {
      readonly kind: 'failed';
      readonly source: 'auth0' | 'login';
      readonly errorMessage: string;
    };

type Auth0SessionStatus =
  | 'checking'
  | 'authenticated'
  | 'anonymous'
  | 'failed';

type UseAuth0LoginFlowOptions = {
  readonly intent: OktaLoginIntent;
  readonly onVerified: (verified: boolean) => void;
};

const IDLE_STATE: Auth0LoginFlowState = {
  status: 'idle',
  errorMessage: null,
};

const PENDING_STATE: Auth0LoginFlowState = {
  status: 'pending',
  errorMessage: null,
};

const IDLE_ATTEMPT_STATE: Auth0LoginAttemptState = { status: 'idle' };

const OKTA_SIGN_IN_ERROR_MESSAGE = 'Unable to start Okta sign-in.';
const OKTA_VERIFICATION_ERROR_MESSAGE = 'Unable to verify Okta user.';

const resolveAuth0SessionStatus = ({
  hasError,
  isAuthenticated,
  isLoading,
}: {
  readonly hasError: boolean;
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
}): Auth0SessionStatus => {
  if (hasError) return 'failed';
  if (isLoading) return 'checking';
  if (isAuthenticated) return 'authenticated';
  return 'anonymous';
};

const resolveLoginPhase = ({
  attemptState,
  hasUser,
  intent,
  sessionStatus,
}: {
  readonly attemptState: Auth0LoginAttemptState;
  readonly hasUser: boolean;
  readonly intent: OktaLoginIntent;
  readonly sessionStatus: Auth0SessionStatus;
}): Auth0LoginFlowPhase => {
  if (sessionStatus === 'failed') {
    return {
      kind: 'failed',
      source: 'auth0',
      errorMessage: OKTA_SIGN_IN_ERROR_MESSAGE,
    };
  }

  const relevantFailureSource =
    intent.kind === 'redirect' ? 'redirect' : 'popup';
  if (
    attemptState.status === 'failed' &&
    (attemptState.source === 'verification' ||
      attemptState.source === relevantFailureSource)
  ) {
    return {
      kind: 'failed',
      source: 'login',
      errorMessage: attemptState.errorMessage,
    };
  }

  switch (sessionStatus) {
    case 'checking':
      return { kind: 'authenticating' };
    case 'authenticated':
      if (hasUser) return { kind: 'verifying' };
      return { kind: 'authenticating' };
    case 'anonymous':
      if (intent.kind === 'redirect') return { kind: 'redirecting' };
      if (
        attemptState.status === 'pending' &&
        attemptState.source === 'popup'
      ) {
        return { kind: 'authenticating' };
      }
      return { kind: 'idle' };
  }
};

const resolveLoginFlowState = (
  phase: Auth0LoginFlowPhase,
): Auth0LoginFlowState => {
  switch (phase.kind) {
    case 'idle':
      return IDLE_STATE;
    case 'failed':
      return { status: 'failed', errorMessage: phase.errorMessage };
    case 'redirecting':
    case 'authenticating':
    case 'verifying':
      return PENDING_STATE;
  }
};

export const useAuth0LoginFlow = ({
  intent,
  onVerified,
}: UseAuth0LoginFlowOptions) => {
  const verifyOktaUser = useUserStore((state) => state.verifyOktaUser);
  const loginAttemptId = useRef(0);
  const verificationStarted = useRef(false);
  const {
    error: auth0Error,
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    loginWithPopup,
    loginWithRedirect,
    user,
  } = useAuth0();
  const sessionStatus = resolveAuth0SessionStatus({
    hasError: Boolean(auth0Error),
    isAuthenticated,
    isLoading,
  });
  const [attemptState, setAttemptState] =
    useState<Auth0LoginAttemptState>(IDLE_ATTEMPT_STATE);
  const phase = resolveLoginPhase({
    attemptState,
    hasUser: Boolean(user),
    intent,
    sessionStatus,
  });
  const state = resolveLoginFlowState(phase);
  const hasAuth0Failed =
    phase.kind === 'failed' && phase.source === 'auth0';
  const shouldStartRedirect = phase.kind === 'redirecting';
  const shouldVerifyUser = phase.kind === 'verifying';

  const failVerification = useCallback(
    (message: string) => {
      verificationStarted.current = false;
      setAttemptState({
        status: 'failed',
        source: 'verification',
        errorMessage: message,
      });
      onVerified(false);
    },
    [onVerified],
  );

  const failLoginAttempt = useCallback(
    (id: number, source: Auth0LoginAttemptSource) => {
      if (loginAttemptId.current !== id) return;

      verificationStarted.current = false;
      setAttemptState({
        status: 'failed',
        source,
        errorMessage: OKTA_SIGN_IN_ERROR_MESSAGE,
      });
      onVerified(false);
    },
    [onVerified],
  );

  const startRedirect = useCallback(() => {
    if (intent.kind !== 'redirect') return;

    const attemptId = ++loginAttemptId.current;
    setAttemptState({
      status: 'pending',
      id: attemptId,
      source: 'redirect',
    });

    void loginWithRedirect(intent.options).catch((error: unknown) => {
      if (loginAttemptId.current !== attemptId) return;

      const message =
        error instanceof Error ? error.message : OKTA_SIGN_IN_ERROR_MESSAGE;
      console.warn('[auth0-login-flow] redirect login failed', message);
      failLoginAttempt(attemptId, 'redirect');
    });
  }, [failLoginAttempt, intent, loginWithRedirect]);

  const startPopup = useCallback(() => {
    const attemptId = ++loginAttemptId.current;
    setAttemptState({ status: 'pending', id: attemptId, source: 'popup' });

    void loginWithPopup()
      .then(() => {
        if (loginAttemptId.current !== attemptId) return;

        setAttemptState((currentState) =>
          currentState.status === 'pending' &&
          currentState.id === attemptId
            ? IDLE_ATTEMPT_STATE
            : currentState,
        );
      })
      .catch((error: unknown) => {
        if (loginAttemptId.current !== attemptId) return;

        const message =
          error instanceof Error ? error.message : OKTA_SIGN_IN_ERROR_MESSAGE;
        console.warn('[auth0-login-flow] popup login failed', message);
        failLoginAttempt(attemptId, 'popup');
      });
  }, [failLoginAttempt, loginWithPopup]);

  const retryVerification = useCallback(() => {
    setAttemptState(IDLE_ATTEMPT_STATE);
  }, []);

  const login = useCallback(() => {
    if (sessionStatus === 'authenticated') {
      retryVerification();
      return;
    }

    if (intent.kind === 'redirect') {
      startRedirect();
      return;
    }

    startPopup();
  }, [intent.kind, retryVerification, sessionStatus, startPopup, startRedirect]);

  useEffect(() => {
    if (!hasAuth0Failed) return;

    loginAttemptId.current += 1;
    verificationStarted.current = false;
    setAttemptState(IDLE_ATTEMPT_STATE);
    console.warn('[auth0-login-flow] Auth0 login failed', auth0Error?.message);
    onVerified(false);
  }, [auth0Error?.message, hasAuth0Failed, onVerified]);

  useEffect(() => {
    if (!shouldStartRedirect) {
      if (
        attemptState.status === 'pending' &&
        attemptState.source === 'redirect' &&
        attemptState.id === loginAttemptId.current
      ) {
        loginAttemptId.current += 1;
      }
      return;
    }
    if (
      attemptState.status === 'pending' &&
      attemptState.source === 'redirect' &&
      attemptState.id === loginAttemptId.current
    ) {
      return;
    }

    startRedirect();
  }, [attemptState, shouldStartRedirect, startRedirect]);

  useEffect(() => {
    if (!shouldVerifyUser || verificationStarted.current) return;

    verificationStarted.current = true;

    const verifyUser = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        const verified = await verifyOktaUser({ accessToken });

        if (!verified) {
          failVerification(OKTA_VERIFICATION_ERROR_MESSAGE);
          return;
        }

        onVerified(true);
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : OKTA_VERIFICATION_ERROR_MESSAGE;
        console.warn('[auth0-login-flow] Okta verification failed', message);
        failVerification(OKTA_VERIFICATION_ERROR_MESSAGE);
      }
    };

    void verifyUser();
  }, [
    failVerification,
    getAccessTokenSilently,
    onVerified,
    shouldVerifyUser,
    verifyOktaUser,
  ]);

  return { ...state, login };
};

export const OktaLoginButton = ({ onClick }: { readonly onClick: () => void }) => {
  const color = useColor();

  return (
    <>
      <Space h="12px" />
      <Button
        sizeType="l"
        variant="outlined"
        onClick={onClick}
        style={{
          width: '100%',
          color: color.gray700,
        }}
      >
        <img
          src="/assets/logo-okta.jpeg"
          alt="okta login"
          className="rounded-md"
          width="20px"
          height="20px"
        />
        Continue with Okta
      </Button>
    </>
  );
};
